'use client';

import {
    createContext,
    useCallback,
    useContext,
    ReactNode,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { BootAuthResponse } from '@/lib/types';
import { connectionLossFeature } from '@/lib/connection/connectionLossFeature';
import { FeatureFlags } from '@/lib/consts/featureFlags';
import { isWebviewBrowser } from '@/lib/utils/webview';
import {
    MaintenanceModeSsePayload,
    mergeAuthBootMaintenanceMode,
} from '@/lib/utils/maintenanceMode';

interface IAuthBootContextValue {
    authBoot: BootAuthResponse;
    isWebview: boolean;
    applyMaintenanceModeSseUpdate: (payload: MaintenanceModeSsePayload) => void;
}

interface IAuthBootProviderProps {
    children: ReactNode;
    authBoot: BootAuthResponse;
    isWebview: boolean;
}

const AuthBootContext = createContext<IAuthBootContextValue | undefined>(undefined);

export function AuthBootProvider({
    children,
    authBoot,
    isWebview: serverIsWebview,
}: IAuthBootProviderProps): React.ReactElement {
    const [authBootState, setAuthBootState] = useState(authBoot);
    const isWebview = useMemo(() => serverIsWebview || isWebviewBrowser(), [serverIsWebview]);

    useEffect(() => {
        setAuthBootState(authBoot);
    }, [authBoot]);

    const applyMaintenanceModeSseUpdate = useCallback(
        (payload: MaintenanceModeSsePayload) => {
            setAuthBootState((current) => mergeAuthBootMaintenanceMode(current, payload));
        },
        [],
    );

    const isConnectionLossEnabled = Boolean(
        authBootState?.featureFlags?.[FeatureFlags.DASHBOARD_CONNECTION_LOSS_MODAL],
    );
    useEffect(() => {
        if (!isConnectionLossEnabled) return;
        connectionLossFeature.setEnabled(true);
    }, [isConnectionLossEnabled]);

    return (
        <AuthBootContext.Provider
            value={{ authBoot: authBootState, isWebview, applyMaintenanceModeSseUpdate }}
        >
            {children}
        </AuthBootContext.Provider>
    );
}

export function useAuthBoot(): IAuthBootContextValue {
    const context = useContext(AuthBootContext);
    if (!context) {
        throw new Error('useAuthBoot must be used within AuthBootProvider');
    }
    return context;
}
